import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateCustomerInfo } from '@store/actions/customerAction';
import SatButton from '@components/common/satUI/SatButton';
import SatModal from '@components/common/satUI/SatModal';
import {
  cleanPhoneNumber,
  convertDateString,
  isEmptyString,
} from '@common/utils';

// { label, cdpKey, losKey, rowSpan, redner }
const CONTACT_TABLE_ROWS = [
  {
    label: '최종변경일시',
    cdpKey: 'contactFinalDateTime',
    losKey: 'lastChgDate',
    render: data =>
      data?.length === 8
        ? convertDateString(data, 'yyyy-MM-dd', 'yyyyMMdd')
        : data,
  },
  {
    label: '고객명',
    cdpKey: 'name',
    losKey: 'name',
  },
  {
    label: '휴대전화',
    cdpKey: 'mobileTelNo',
    losKey: 'cellphoneNo',
  },
  {
    label: '이메일',
    cdpKey: 'emailAddr',
    losKey: 'email',
  },
  {
    label: '자택전화',
    cdpKey: 'homeTelNo',
    losKey: 'homeTelNo',
  },
  {
    label: '직장전화',
    cdpKey: 'wkplTelNo',
    losKey: 'workplaceTelNo',
  },
  {
    label: '우편물수령처',
    cdpKey: 'postRcvTpCd',
    losKey: 'mailReceivePlac',
    render: data =>
      isEmptyString(data)
        ? ''
        : String(data) === '1'
        ? '자택'
        : String(data) === '2'
        ? '직장'
        : String(data) === '11'
        ? '선택없음'
        : '기타',
  },
  {
    label: '자택주소',
    cdpKey: 'homeZipCd', // 우편번호
    losKey: 'homeZipCd',
    rowSpan: 3,
  },
  {
    label: null,
    cdpKey: 'homeAddr', // 주소
    losKey: 'homeAddr1',
  },
  {
    label: null,
    cdpKey: 'homeAddrDtl', // 상세주소
    losKey: 'homeAddr2',
  },
  {
    label: '직장주소',
    cdpKey: 'wkplZipCd', // 우편번호
    losKey: 'workplaceZipCd',
    rowSpan: 3,
  },
  {
    label: null,
    cdpKey: 'wkplAddr', // 주소
    losKey: 'workplaceAddr1',
  },
  {
    label: null,
    cdpKey: 'wkplAddrDtl', // 상세주소
    losKey: 'workplaceAddr2',
  },
];

const LosContactButton = ({ customerInfo = {} }) => {
  const dispatch = useDispatch();
  const { getCdpContactInfo, getLosContactInfo, changeCdpCustomerInfo } =
    $hooks.useApi();
  const { alert, confirm } = $hooks.useModal();
  const { info: toastInfo, error: toastError } = $hooks.useToast();
  const {
    isOn: isShowModal,
    on: showModal,
    off: hideModal,
  } = $hooks.useToggler();
  const [cdpContactInfo, setCdpContactInfo] = useState(null);
  const [losContactInfo, setLosContactInfo] = useState(null);

  const convertDateFormat = dateString =>
    dateString?.length === 8
      ? convertDateString(dateString, 'yyyy-MM-dd', 'yyyyMMdd')
      : dateString;

  const setContactData = async () => {
    try {
      const custNo = customerInfo.custNo;
      const [cdpContactResponse, losContactResponse] = await Promise.all([
        getCdpContactInfo({ params: { custNo } }),
        getLosContactInfo({ params: { custNo } }),
      ]);

      const cdpData = $utils.getApiData(cdpContactResponse);
      const losData = $utils.getApiData(losContactResponse);

      // 필요한 데이터 형태 변경 (예] 최종변경일시 전화번호, 휴대폰번호)
      if (cdpData['contactFinalDateTime'])
        cdpData['contactFinalDateTime'] = convertDateFormat(
          cdpData['contactFinalDateTime'],
        );
      if ( (cdpData['postRcvTpCd'] || '') === '0' || (cdpData['postRcvTpCd'] || '') === '')
        cdpData['postRcvTpCd'] = '11';
      if (losData['lastChgDate'])
        losData['lastChgDate'] = convertDateFormat(losData['lastChgDate']);
      if (losData['cellphoneNo'])
        losData['cellphoneNo'] = cleanPhoneNumber(losData['cellphoneNo']);
      if (losData['homeTelNo'])
        losData['homeTelNo'] = cleanPhoneNumber(losData['homeTelNo']);
      if (losData['workplaceTelNo'])
        losData['workplaceTelNo'] = cleanPhoneNumber(losData['workplaceTelNo']);
      // 우편물 수령처가 자택('1'), 직장('2')가 아니면 제거 (빈 값으로 치환)
      if (losData['mailReceivePlac'])
        losData['mailReceivePlac'] = ['2', '3'].includes(
          String(losData['mailReceivePlac']),
        )
          ? (String(losData['mailReceivePlac']) === '3' ? '1' : '2')
          : '';

      // 데이터 세팅
      setCdpContactInfo(cdpData);
      setLosContactInfo(losData);

      return true;
    } catch (error) {
      console.log('setContactData() - error', error);
      return false;
    }
  };

  const checkLosDataExist = () => {
    // 변경을 확인 할 Key 들 [최종변경일시(contactFinalDateTime) 제외]
    const keys = CONTACT_TABLE_ROWS.map(row => row.losKey).filter(
      key => key !== 'contactFinalDateTime',
    );
    const isLosDataExist = keys.some(
      key => !$utils.isEmptyString(losContactInfo[key]),
    );

    return isLosDataExist;
  };

  const getRowClassName = ({ cdpKey, losKey }) => {
    const cdpData = cdpContactInfo[cdpKey];
    const losData = losContactInfo[losKey];

    // 연락처 정보의 cdpData와 losData가 다르면 'c-orange' 클래스 입력
    if (cdpData !== losData) return 'c-orange';
    return '';
  };

  const handleShowModal = async () => {
    const isSuccess = await setContactData();

    if (isSuccess) showModal();
    else
      alert({
        message:
          '고객 정보를 조회할 수 없습니다. 주민등록번호가 정정된 고객님의 경우 고객등록 사이트를 통해 본인인증을 다시 진행해 주세요.',
      });
  };

  const updateContactInfo = async () => {
    try {
      const newCustomerInfo = { ...customerInfo };

      CONTACT_TABLE_ROWS.forEach(row => {
        const { cdpKey, losKey } = row;
        const customerKeys = Object.keys(newCustomerInfo);
        const isKeyExist = customerKeys.includes(cdpKey);
        const customerData = newCustomerInfo[cdpKey];
        const losData = losContactInfo[losKey];

        // 고객 데이터에 cdpKey 값인 Key가 존재하면서 LOS 데이터와 다른 것만 업데이트
        if (isKeyExist && customerData !== losData)
          newCustomerInfo[cdpKey] = losData;
      });
    
      // LOS 연락처 정보로 고객 상세 정보 업데이트
      const response = await changeCdpCustomerInfo({ payload: newCustomerInfo });
      const isSuccess = $utils.checkApiSuccess(response);
      if (!isSuccess) throw new Error('LOS 연락처 정보 업데이트 에러');

      dispatch(updateCustomerInfo(newCustomerInfo));
      toastInfo('LOS 연락처 정보 업데이트에 성공 하였습니다.');
      hideModal();
    } catch (error) {
      console.log('updateContactInfo() - error', error);
      toastError('LOS 연락처 정보 업데이트에 실패 하였습니다.');
    }
  };

  const handleUpdateFromLos = async () => {
    // CDP, LOS 데이터를 같은 키 값(CDP Key)인 객체로 만들어 비교
    const [cdpInfo, losInfo] = CONTACT_TABLE_ROWS.reduce(
      (entries, { cdpKey, losKey }) => [
        { ...entries[0], [cdpKey]: cdpContactInfo[cdpKey] },
        { ...entries[1], [cdpKey]: losContactInfo[losKey] },
      ],
      [{}, {}],
    );
    const isDataChanged = $utils.checkChanged(cdpInfo, losInfo, {
      exceptions: ['lastChgDate'],
      isSkipUndefined: true,
    });

    if (!isDataChanged) alert({ message: '변경된 부분이 없습니다.' });
    else
      confirm({
        message: 'LOS 정보로 업데이트 하시겠습니까?',
        onClose: isConfirm => {
          if (!isConfirm) return;
          updateContactInfo();
        },
      });
  };

  // 데이터 세팅하여 버튼 노출 여부 확인
  useEffect(setContactData, []);

  return (
    <>
      {cdpContactInfo && losContactInfo && checkLosDataExist() && (
        <>
          <SatButton
            label="LOS 연락처 정보"
            styleClass="btn-s blue"
            onClick={handleShowModal}
          />

          <SatModal wide visible={isShowModal} setVisible={hideModal}>
            <>
              <header className="modal-header">
                <h3 className="layer-tit2">
                  {customerInfo.name}님의 연락처정보
                </h3>
              </header>

              <div className="cont">
                <div className="modal-scroll-wrap">
                  <table className="tbl-sty5 text-center mgt-8">
                    <caption>타사가입금액 합산한도체크</caption>

                    <colgroup>
                      <col width="100px" />
                      <col width="40%" />
                      <col width="40%" />
                    </colgroup>

                    <thead>
                      <tr>
                        <th />
                        <th>현재 연락처정보</th>
                        <th>LOS 연락처정보</th>
                      </tr>
                    </thead>

                    <tbody>
                      {CONTACT_TABLE_ROWS.map(
                        ({ label, cdpKey, losKey, rowSpan, render }, index) => (
                          <tr key={index}>
                            {label && <th rowSpan={rowSpan}>{label}</th>}
                            <td>
                              {render
                                ? render(cdpContactInfo[cdpKey])
                                : cdpContactInfo[cdpKey]}
                            </td>
                            <td className={getRowClassName({ cdpKey, losKey })}>
                              {render
                                ? render(losContactInfo[losKey])
                                : losContactInfo[losKey]}
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>

                  <p className="text-info2 ico-type">
                    [← LOS 정보로 동기화]를 누르면 기존 LAS 정보가 LOS 정보로
                    일괄 업데이트됩니다.
                  </p>
                </div>
              </div>

              <div className="layer-footer d-flex-center flex-gap-small">
                <SatButton
                  label="닫기"
                  styleClass="btn-s blue btn-wide"
                  onClick={hideModal}
                />
                <SatButton
                  label="← LOS 정보로 동기화"
                  styleClass="btn-s blue btn-wide"
                  onClick={handleUpdateFromLos}
                />
              </div>
            </>
          </SatModal>
        </>
      )}
    </>
  );
};

export default LosContactButton;
